/**
 * Reiter *Stammdaten* für einen Mieter (Spec 03, „Reiter Stammdaten eines
 * Mieters"). Struktur und Verhalten spiegeln die drei Arten aus 02a
 * (`UnitMasterData` u. a.): Container besitzt die Query, diese Komponente den
 * lokalen Bearbeitungszustand und die Mutationen.
 *
 * Der Titel der Objektansicht (`tenant-label`) ist `entities.label` — und
 * damit `display_name` **ohne** Anrede (Spec 03, „Gemeinsam für beide neuen
 * Arten"); die Anrede steht ausschließlich hier im Feld `tenant-edit-salutation`.
 */

import type { UseQueryResult } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ArchiveControls } from '@/components/ArchiveControls'
import { ArchivedBanner } from '@/components/ArchivedBanner'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import type { TenantDetail, TenantUpdate } from '@/features/tenants/api'
import { useArchivePreview, useArchiveTenant, useUnarchiveTenant, useUpdateTenant } from '@/features/tenants/api'
import { FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { LegalFormField } from '@/features/tenants/LegalFormField'
import { SalutationField } from '@/features/tenants/SalutationField'

interface TenantMasterDataProps {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  /** `ObjectHead.archived_by_id`/`archived_by_label` — kommen von
   * `routes/ObjectDetail.tsx`, nicht von `TenantDetail` (I9). */
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly query: UseQueryResult<TenantDetail>
}

interface FormValues {
  readonly displayName: string
  readonly salutation: string
  readonly legalForm: string
  readonly email: string
  readonly phone: string
  readonly billingAddress: string
  readonly iban: string
  readonly sepaMandateRef: string
  readonly notes: string
}

function deriveForm(tenant: TenantDetail): FormValues {
  return {
    displayName: tenant.display_name,
    salutation: tenant.salutation?.value ?? '',
    legalForm: tenant.legal_form.value,
    email: tenant.email ?? '',
    phone: tenant.phone ?? '',
    billingAddress: tenant.billing_address ?? '',
    iban: tenant.iban ?? '',
    sepaMandateRef: tenant.sepa_mandate_ref ?? '',
    notes: tenant.notes ?? '',
  }
}

function TenantReadField({ label, value, testId }: { readonly label: string; readonly value: string | null; readonly testId: string }) {
  return <div><dt className="text-label text-muted">{label}</dt><dd data-testid={testId} className="text-body text-text">{value ?? '—'}</dd></div>
}

export function TenantMasterData({ entityId, canEdit, canUnarchive, archivedById, archivedByLabel, query }: TenantMasterDataProps) {
  return (
    <section data-testid="tenant-master-data" className="px-(--page-x) py-4">
      {query.isPending && <SkeletonRows testId="tenant-master-data-loading" rows={9} />}

      {query.isError && (
        <ErrorState
          testId="tenant-master-data-error"
          retryTestId="tenant-master-data-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && (
        <TenantMasterDataForm
          entityId={entityId}
          canEdit={canEdit}
          canUnarchive={canUnarchive}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          tenant={query.data}
        />
      )}
    </section>
  )
}

function TenantMasterDataForm({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  tenant,
}: {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly tenant: TenantDetail
}) {
  const updateMutation = useUpdateTenant()
  const archiveMutation = useArchiveTenant()
  const unarchiveMutation = useUnarchiveTenant()
  const archivePreviewMutation = useArchivePreview()

  const [form, setForm] = useState<FormValues>(() => deriveForm(tenant))
  const [initial, setInitial] = useState<FormValues>(() => deriveForm(tenant))
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const [syncedKey, setSyncedKey] = useState(`${tenant.id}:${String(tenant.version)}`)
  const currentKey = `${tenant.id}:${String(tenant.version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    const next = deriveForm(tenant)
    setForm(next)
    setInitial(next)
  }

  const isArchived = tenant.archived_at !== null
  const isCompany = form.legalForm === 'company'
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial)

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function openEditor() {
    const next = deriveForm(tenant)
    setForm(next)
    setInitial(next)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  /** Dieselbe Formularregel wie im Anlegen-Sheet (AK-202): „Firma" schaltet
   * die Anrede ab und leert sie — der Server prüft es ohnehin (422), das
   * Formular erspart nur den Fehlversuch. */
  function updateLegalForm(value: string) {
    setForm((current) => ({ ...current, legalForm: value, salutation: value === 'company' ? '' : current.salutation }))
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await updateMutation.mutateAsync({
        tenantId: entityId,
        ifMatch: String(tenant.version),
        body: {
          display_name: form.displayName,
          // Direkt `null` statt `clear` — funktioniert im Web für jedes
          // leerbare Feld (Spec 02a, „Felder leeren": `clear` ist die
          // iOS-Ergänzung, weil Swifts `Codable` ein fehlendes Feld nicht von
          // `null` unterscheidet, „keiner davon eine iOS-Aufgabe" auf Web).
          salutation: isCompany || form.salutation === '' ? null : (form.salutation as NonNullable<TenantUpdate['salutation']>),
          legal_form: form.legalForm as NonNullable<TenantUpdate['legal_form']>,
          email: form.email.trim() === '' ? null : form.email.trim(),
          phone: form.phone.trim() === '' ? null : form.phone.trim(),
          billing_address: form.billingAddress.trim() === '' ? null : form.billingAddress.trim(),
          iban: form.iban.trim() === '' ? null : form.iban.trim(),
          sepa_mandate_ref: form.sepaMandateRef.trim() === '' ? null : form.sepaMandateRef.trim(),
          notes: form.notes.trim() === '' ? null : form.notes.trim(),
        },
      })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleUnarchive() {
    setArchiveError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ tenantId: entityId, ifMatch: String(tenant.version) })
    } catch (error) {
      setArchiveError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <dl data-testid="tenant-master-data-read" className="grid gap-3 sm:grid-cols-2">
          <TenantReadField label="Name" value={tenant.display_name} testId="tenant-read-display-name" />
          <TenantReadField label="Art" value={tenant.legal_form.label} testId="tenant-read-legal-form" />
          <TenantReadField label="Anrede" value={tenant.salutation?.label ?? null} testId="tenant-read-salutation" />
          <TenantReadField label="E-Mail" value={tenant.email} testId="tenant-read-email" />
          <TenantReadField label="Telefon" value={tenant.phone} testId="tenant-read-phone" />
          <TenantReadField label="Rechnungsanschrift" value={tenant.billing_address} testId="tenant-read-billing-address" />
          <TenantReadField label="IBAN" value={tenant.iban} testId="tenant-read-iban" />
          <TenantReadField label="SEPA-Mandatsreferenz" value={tenant.sepa_mandate_ref} testId="tenant-read-sepa-mandate-ref" />
          <TenantReadField label="Notizen" value={tenant.notes} testId="tenant-read-notes" />
        </dl>
        {canEdit && !isArchived && (
          <button type="button" ref={editTriggerRef} data-testid="tenant-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      {isArchived ? (
        <ArchivedBanner
          kind="tenant"
          canUnarchive={canUnarchive}
          parentId={null}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          onUnarchive={() => void handleUnarchive()}
          isUnarchiving={unarchiveMutation.isPending}
        />
      ) : (
        <ArchiveControls
          kind="tenant"
          entityId={entityId}
          label={tenant.label}
          version={tenant.version}
          parentId={null}
          isArchiving={archiveMutation.isPending}
          onArchive={(ifMatch) => archiveMutation.mutateAsync({ tenantId: entityId, ifMatch })}
          onError={setArchiveError}
          archivePreview={archivePreviewMutation}
        />
      )}
      {archiveError !== undefined && <p data-testid="tenant-archive-error" role="alert" className="text-label text-status-overdue">{archiveError}</p>}
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Mieter bearbeiten"
        triggerRef={editTriggerRef}
        formId="tenant-edit-form"
        saveTestId="tenant-edit-save"
        saveDisabled={!isDirty || updateMutation.isPending}
        isSaving={updateMutation.isPending}
      >
    <form id="tenant-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
      <FormField
        label="Name"
        testId="tenant-edit-display-name"
        apiField="display_name"
        errorPrefix="tenant-edit"
        required
        disabled={isArchived}
        value={form.displayName}
        onChange={updateField('displayName')}
        error={fieldError}
      />
      <SalutationField
        testId="tenant-edit-salutation"
        errorPrefix="tenant-edit"
        value={form.salutation}
        onChange={updateField('salutation')}
        error={fieldError}
        disabled={isArchived || isCompany}
      />
      <LegalFormField
        testId="tenant-edit-legal-form"
        errorPrefix="tenant-edit"
        disabled={isArchived}
        value={form.legalForm}
        onChange={updateLegalForm}
        error={fieldError}
      />
      <FormField
        label="E-Mail"
        testId="tenant-edit-email"
        apiField="email"
        errorPrefix="tenant-edit"
        type="email"
        disabled={isArchived}
        value={form.email}
        onChange={updateField('email')}
        error={fieldError}
      />
      <FormField
        label="Telefon"
        testId="tenant-edit-phone"
        apiField="phone"
        errorPrefix="tenant-edit"
        type="tel"
        disabled={isArchived}
        value={form.phone}
        onChange={updateField('phone')}
        error={fieldError}
      />
      <FormTextareaField
        label="Rechnungsanschrift"
        testId="tenant-edit-billing-address"
        apiField="billing_address"
        errorPrefix="tenant-edit"
        disabled={isArchived}
        value={form.billingAddress}
        onChange={updateField('billingAddress')}
        error={fieldError}
      />
      <FormField
        label="IBAN"
        testId="tenant-edit-iban"
        apiField="iban"
        errorPrefix="tenant-edit"
        disabled={isArchived}
        value={form.iban}
        onChange={updateField('iban')}
        error={fieldError}
      />
      <FormField
        label="SEPA-Mandatsreferenz"
        testId="tenant-edit-sepa-mandate-ref"
        apiField="sepa_mandate_ref"
        errorPrefix="tenant-edit"
        disabled={isArchived}
        value={form.sepaMandateRef}
        onChange={updateField('sepaMandateRef')}
        error={fieldError}
      />
      <FormTextareaField
        label="Notizen"
        testId="tenant-edit-notes"
        apiField="notes"
        errorPrefix="tenant-edit"
        disabled={isArchived}
        value={form.notes}
        onChange={updateField('notes')}
        error={fieldError}
      />

      {generalError !== undefined && (
        <p data-testid="tenant-edit-error" role="alert" className="text-label text-status-overdue">
          {generalError}
        </p>
      )}

    </form>
      </MasterDataEditSheet>
      <ConfirmationStrip message={confirmation} onDismiss={() => { setConfirmation(null) }} testId="object-save-confirmation" />
    </>
  )
}
